import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  Box, 
  Paper,
  TextField,
  Button,
  Snackbar,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  MenuItem,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  PersonAdd as PersonAddIcon,
  CorporateFare as CorporateFareIcon,
  Work as WorkIcon,
  ArrowForward as ArrowForwardIcon,
  LocationOn as LocationOnIcon
} from '@mui/icons-material';
import axios from 'axios';
import './App.css';

// إنشاء axios instance
const api = axios.create({
  baseURL: 'https://filesregsiteration.sstli.com',
  headers: {
    'Content-Type': 'application/json',
  }
});

// API للفروع
const branchesApi = axios.create({
  baseURL: 'https://api4.sstli.com',
  headers: {
    'Content-Type': 'application/json',
  }
});

function App() {
  const [formData, setFormData] = useState({
    name: '',
    job_title: '',
    branch: ''
  });
  
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  
  const [snackbar, setSnackbar] = useState({ 
    open: false, 
    message: '', 
    severity: 'success' 
  });
  
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [userSubmitted, setUserSubmitted] = useState(false);

  // جلب الفروع من الـ API
  const fetchBranches = async () => {
    try {
      const response = await branchesApi.get('/api/branches/all');
      if (response.data) {
        // نحول الـ branches لـ array من الأسماء فقط
        const branchNames = response.data.map(branch => branch.name);
        setBranches(branchNames);
      }
    } catch (error) {
      console.error('Error fetching branches:', error);
      showSnackbar('حدث خطأ في جلب قائمة الفروع', 'error');
      // نضيف فروع افتراضية في حالة الخطأ
      setBranches([
        'فرع حفر الباطن حي المصيف',
        'فرع الدمام حي الشاطئ',
        'فرع الرياض حي الحمراء',
        'فرع المدينة المنورة',
        'فرع خميس مشيط'
      ]);
    } finally {
      setLoadingBranches(false);
    }
  };

  // عند تحميل الصفحة، نشوف إذا المستخدم سجل قبل كده ونجلب الفروع
  useEffect(() => {
    const hasSubmitted = localStorage.getItem('employee_submitted');
    const savedData = localStorage.getItem('employee_data');
    
    if (hasSubmitted === 'true' && savedData) {
      setUserSubmitted(true);
      setSubmittedData(JSON.parse(savedData));
    }
    
    // جلب قائمة الفروع
    fetchBranches();
  }, []);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // تحقق من البيانات
    if (!formData.name.trim() || !formData.job_title.trim() || !formData.branch.trim()) {
      showSnackbar('من فضلك قم بتعبئة جميع الحقول', 'error');
      return;
    }

    setLoading(true);

    try {
      const response = await api.post('/employees.php?action=create', {
        name: formData.name,
        job_title: formData.job_title,
        branch: formData.branch
      });
      
      if (response.data.success) {
        // حفظ في localStorage
        localStorage.setItem('employee_submitted', 'true');
        localStorage.setItem('employee_data', JSON.stringify(formData));
        
        setSubmittedData(formData);
        setUserSubmitted(true);
        setSubmitted(true);
        
        showSnackbar('✅ تم تسجيل بياناتك بنجاح!', 'success');
        
        // إعادة تعيين الفورم
        setFormData({
          name: '',
          job_title: '',
          branch: ''
        });
      } else {
        showSnackbar(response.data.error || 'حدث خطأ أثناء التسجيل', 'error');
      }
    } catch (error) {
      console.error('Submission error:', error);
      showSnackbar('حدث خطأ في الاتصال بالسيرفر', 'error');
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const handleNewRegistration = () => {
    localStorage.removeItem('employee_submitted');
    localStorage.removeItem('employee_data');
    setUserSubmitted(false);
    setSubmitted(false);
    setSubmittedData(null);
  };

  // إذا المستخدم سجل قبل كده، نعرض له شاشة التأكيد
  if (userSubmitted && submittedData) {
    return (
      <div className="App" style={{ fontFamily: "'Cairo', sans-serif" }}>
        <Container maxWidth="md" sx={{ mt: 8 }}>
          <Paper 
            elevation={0} 
            sx={{ 
              p: 5, 
              textAlign: 'center',
              borderRadius: 4,
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white'
            }}
          >
            <CheckCircleIcon sx={{ fontSize: 80, mb: 3, color: '#4CAF50' }} />
            
            <Typography variant="h3" fontWeight="bold" gutterBottom>
              🎉 تم التسجيل بنجاح!
            </Typography>
            
            <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
              شكراً لك على تسجيل بياناتك
            </Typography>

            <Card sx={{ maxWidth: 500, mx: 'auto', mb: 4, borderRadius: 3 }}>
              <CardContent>
                <Typography variant="h6" color="primary" gutterBottom>
                  📋 بياناتك المسجلة
                </Typography>
                
                <Box sx={{ textAlign: 'right', mt: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <PersonAddIcon sx={{ mr: 2, color: '#2196F3' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        الاسم
                      </Typography>
                      <Typography variant="h6">
                        {submittedData.name}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <WorkIcon sx={{ mr: 2, color: '#FF9800' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        المسمى الوظيفي
                      </Typography>
                      <Typography variant="h6">
                        {submittedData.job_title}
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: '#f5f5f5', borderRadius: 2 }}>
                    <CorporateFareIcon sx={{ mr: 2, color: '#4CAF50' }} />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography variant="body2" color="textSecondary">
                        الفرع
                      </Typography>
                      <Typography variant="h6">
                        {submittedData.branch}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </CardContent>
            </Card>

            <Typography variant="body1" sx={{ mb: 4, color: 'rgba(255,255,255,0.8)' }}>
              ⚠️ لقد قمت بالتسجيل بالفعل. لا يمكنك التسجيل مرة أخرى.
            </Typography>
          </Paper>
        </Container>

        <Snackbar 
          open={snackbar.open} 
          autoHideDuration={4000} 
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseSnackbar} 
            severity={snackbar.severity}
            sx={{ borderRadius: 2, fontFamily: "'Cairo', sans-serif" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </div>
    );
  }

  // إذا المستخدم ماسجلش، نعرض له الفورم
  return (
    <div className="App" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <Container maxWidth="md" sx={{ mt: 4, mb: 8 }}>
        {/* الهيدر */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography 
            variant="h2" 
            fontWeight="bold" 
            sx={{ 
              mb: 2,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent'
            }}
          >
            📝 تسجيل بيانات الموظفين
          </Typography>
          
          <Typography variant="h5" color="textSecondary" sx={{ mb: 4 }}>
            من فضلك قم بتعبئة النموذج أدناه لتسجيل بياناتك
          </Typography>
        </Box>

        {/* الفورم */}
        <Paper 
          elevation={3} 
          sx={{ 
            p: { xs: 3, md: 5 }, 
            borderRadius: 4,
            background: 'linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%)'
          }}
        >
          <form onSubmit={handleSubmit}>
            <Box sx={{ mb: 4 }}>
              <Typography variant="h5" fontWeight="600" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <PersonAddIcon sx={{ mr: 1, color: '#2196F3' }} />
                البيانات الشخصية
              </Typography>
              
              <TextField
                fullWidth
                label="الاسم الكامل"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{ mb: 3 }}
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: 'white' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: "'Cairo', sans-serif" }
                }}
              />
              
              <TextField
                fullWidth
                label="المسمى الوظيفي"
                name="job_title"
                value={formData.job_title}
                onChange={handleChange}
                required
                variant="outlined"
                sx={{ mb: 3 }}
                InputProps={{
                  sx: { borderRadius: 2, bgcolor: 'white' }
                }}
                InputLabelProps={{
                  sx: { fontFamily: "'Cairo', sans-serif" }
                }}
              />
              
              <FormControl fullWidth sx={{ mb: 4 }}>
                <InputLabel id="branch-select-label" sx={{ fontFamily: "'Cairo', sans-serif" }}>
                  اختر الفرع
                </InputLabel>
                <Select
                  labelId="branch-select-label"
                  id="branch-select"
                  name="branch"
                  value={formData.branch}
                  label="اختر الفرع"
                  onChange={handleChange}
                  required
                  sx={{ 
                    borderRadius: 2,
                    bgcolor: 'white',
                    textAlign: 'right',
                    fontFamily: "'Cairo', sans-serif"
                  }}
                  startAdornment={
                    loadingBranches ? (
                      <CircularProgress size={20} sx={{ ml: 2 }} />
                    ) : (
                      <LocationOnIcon sx={{ ml: 2, color: '#4CAF50' }} />
                    )
                  }
                >
                  <MenuItem value="" sx={{ fontFamily: "'Cairo', sans-serif" }}>
                    <em>اختر الفرع</em>
                  </MenuItem>
                  {loadingBranches ? (
                    <MenuItem disabled>
                      <CircularProgress size={20} sx={{ mr: 2 }} />
                      جاري تحميل الفروع...
                    </MenuItem>
                  ) : (
                    branches.map((branch, index) => (
                      <MenuItem 
                        key={index} 
                        value={branch}
                        sx={{ 
                          fontFamily: "'Cairo', sans-serif",
                          direction: 'rtl',
                          textAlign: 'right'
                        }}
                      >
                        {branch}
                      </MenuItem>
                    ))
                  )}
                </Select>
              </FormControl>
            </Box>

            <Box sx={{ textAlign: 'center' }}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                size="large"
                disabled={loading || loadingBranches}
                startIcon={!loading && <ArrowForwardIcon />}
                sx={{ 
                  borderRadius: 3,
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  boxShadow: '0 4px 20px rgba(33, 150, 243, 0.3)',
                  '&:hover': {
                    boxShadow: '0 6px 25px rgba(33, 150, 243, 0.5)',
                    transform: 'translateY(-2px)'
                  },
                  transition: 'all 0.3s ease'
                }}
              >
                {loading ? (
                  <>
                    <CircularProgress size={24} sx={{ mr: 1, color: 'white' }} />
                    جاري التسجيل...
                  </>
                ) : (
                  'تسجيل البيانات ✅'
                )}
              </Button>
              
              <Typography variant="body2" color="textSecondary" sx={{ mt: 3 }}>
                ⚠️ يمكنك التسجيل مرة واحدة فقط
              </Typography>
            </Box>
          </form>
        </Paper>

      </Container>

      {/* الرسائل */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={4000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ borderRadius: 2, fontFamily: "'Cairo', sans-serif" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

export default App;